/**
 * Gym-owner account setup links — minting and expired-link recovery.
 *
 * A newly approved gym owner is emailed a one-time `recovery` link that lands
 * on /onboarding/setup-password so they can pick a password. The token lives
 * for `mailer_otp_exp` (24 hours in production since 2026-08-09; see @cheervue/auth-link-lifetime), but owners routinely open the
 * email hours later — an afternoon approval is often read that evening, and
 * gyms span every US time zone. When the token has expired, /api/auth/callback
 * used to send them to /signin, which is a dead end: the owner has never had a
 * password, so there is nothing there for them to do. Alaska Rage Cheer
 * (2026-08-05) was approved at 16:01 ET and clicked at 01:08 ET; the owner had
 * to discover "Forgot password" unaided to get in.
 *
 * This module is the owner-side twin of `claimLinkRefresh.ts` (#766 U8c, the
 * same dead-end fixed for roster-import parents): on a failed verification the
 * callback routes here instead, to a landing page that emails a fresh link.
 *
 * Both halves live together on purpose. `buildOrgSetupUrl` is the single place
 * a setup link is shaped, and `isOrgSetupNext` recognises one coming back after
 * it died — keeping them in one file is what stops the detector from drifting
 * away from the minter and silently sending owners back to the dead end.
 */

/**
 * The `next` carried by every gym-owner setup link.
 *
 * Roster-import claim links land on the SAME /onboarding/setup-password screen
 * but continue to /dashboard, so the inner destination — not the screen — is
 * what distinguishes an owner link from a parent's. Claim links are matched
 * ahead of this by their `claim_org` param; this constant is the discriminator
 * for everything else.
 */
export const ORG_SETUP_NEXT_PATH = '/onboarding/setup-password?next=/onboarding/select-role';

/** Public landing that offers to email a fresh setup link. */
export const SETUP_LINK_EXPIRED_PATH = '/onboarding/setup-link-expired';

/**
 * Build a gym-owner setup URL from a GoTrue `hashed_token`.
 *
 * Routed through /api/auth/callback rather than the Supabase-hosted
 * `action_link`: the link is generated server-side, so there is no PKCE
 * `code_verifier` cookie for the hosted page to exchange. The callback's
 * `verifyOtp` path needs no such cookie.
 */
export function buildOrgSetupUrl(baseUrl: string, tokenHash: string): string {
  const next = encodeURIComponent(ORG_SETUP_NEXT_PATH);
  // hashed_token is hex today, but its encoding is a GoTrue internal — encode
  // defensively so a future base64url token can't malform the URL.
  return `${baseUrl}/api/auth/callback?token_hash=${encodeURIComponent(tokenHash)}&type=recovery&next=${next}`;
}

/**
 * True when `next` is the destination of a gym-owner setup link.
 *
 * `next` arrives already decoded and open-redirect-checked by
 * `validateInternalPath`, so this compares the decoded form.
 */
export function isOrgSetupNext(next: string | null | undefined): boolean {
  return next === ORG_SETUP_NEXT_PATH;
}

/** The screen every first-time password link lands on, owner or parent. */
const SETUP_PASSWORD_PATH = '/onboarding/setup-password';

/**
 * True when `next` points at the choose-your-first-password screen at all,
 * whatever it continues to afterwards.
 *
 * Deliberately looser than `isOrgSetupNext`, and used only where the question
 * is "has this person ever had a password?" rather than "is this exactly the
 * link we mint today". The exact-match version is the one to route on; this one
 * is a safety net on /signin, which only sees a link whose own landing already
 * declined it — an older-format owner link, or a claim link that somehow lost
 * its `claim_org`. Those people still have no password, so the setup wording is
 * right for them even when the precise `next` is not one we recognise.
 *
 * What it must NOT match is `/setup`, the password-RESET destination. That is
 * the whole reason this is a prefix test on the real screen path instead of the
 * `next.includes('/setup')` it replaces: `/setup` is a substring of
 * `/setup-password`, so the old test caught resets too and told established gym
 * owners to finish setting up an account they already had.
 */
export function isOrgSetupDestination(next: string | null | undefined): boolean {
  return typeof next === 'string' && next.startsWith(SETUP_PASSWORD_PATH);
}

/**
 * Build the redirect for an expired/used owner setup link, or null when `next`
 * belongs to some other flow (the caller then falls back to its normal
 * /signin redirect).
 *
 * No identifier rides along: the landing page asks the owner for their email
 * and hands it to the rate-limited, non-enumerable resend endpoint. An email
 * address must never be put in a URL, and the org id would buy nothing here —
 * unlike the parent claim flow, the resend endpoint resolves the account from
 * the address alone.
 */
export function buildSetupExpiredRedirect(
  next: string | null | undefined,
  baseUrl: string
): URL | null {
  if (!isOrgSetupNext(next)) return null;
  return new URL(SETUP_LINK_EXPIRED_PATH, baseUrl);
}
